import { Component, type ErrorInfo, type ReactNode } from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';
import { track } from '@vercel/analytics';

interface ErrorBoundaryProps extends WithTranslation {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

class ErrorBoundaryBase extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
        try {
            track('app_crash', {
                message: error.message,
                component: errorInfo.componentStack?.split('\n')[1]?.trim() ?? 'unknown',
            });
        } catch {
            // Telemetry must never mask the original crash.
        }
    }

    render() {
        if (this.state.hasError) {
            const { t, i18n } = this.props;
            return (
                <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-background px-6 text-center">
                    <h1 className="text-2xl font-semibold text-primary">
                        {t('errorBoundary.title')}
                    </h1>
                    <p className="text-primary opacity-70 max-w-md">
                        {t('errorBoundary.description')}
                    </p>
                    <a
                        href={`/${i18n.language || 'en'}`}
                        className="button inline-flex items-center px-6 py-3 bg-primary text-secondary rounded-lg font-medium transition-all duration-200 hover:opacity-90"
                    >
                        {t('errorBoundary.action')}
                    </a>
                </div>
            );
        }

        return this.props.children;
    }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryBase);
